if (!window.LimoEditorialFeaturedSections) {
  window.LimoEditorialFeaturedSections = {};
}

class LimoEditorialFeaturedMobileSlider {
  constructor(sectionEl) {
    this.sectionEl = sectionEl;
    this.gridEl = sectionEl.querySelector('.limo-editorial-featured__grid');
    this.paginationEl = sectionEl.querySelector('[data-limo-editorial-featured-pagination]');

    if (!this.gridEl || !this.paginationEl) return;

    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this.sliderEnabled = sectionEl.getAttribute('data-mobile-slider-enabled') === 'true';
    this.pageCount = 0;
    this.activePage = 0;
    this.snapPoints = [];

    this.isPointerDown = false;
    this.dragMoved = false;
    this.isDragScrolling = false;
    this.suppressClick = false;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.startScrollLeft = 0;

    this.handleResize = this.handleResize.bind(this);
    this.handleGridScroll = this.handleGridScroll.bind(this);
    this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleGridClickCapture = this.handleGridClickCapture.bind(this);
    this.handleNativeDragStart = this.handleNativeDragStart.bind(this);

    this.init();
  }

  init() {
    this.gridEl.addEventListener('scroll', this.handleGridScroll, { passive: true });
    this.gridEl.addEventListener('mousedown', this.handlePointerDown);
    this.gridEl.addEventListener('click', this.handleGridClickCapture, true);
    this.gridEl.addEventListener('dragstart', this.handleNativeDragStart);
    window.addEventListener('mousemove', this.handlePointerMove);
    window.addEventListener('mouseup', this.handlePointerUp);
    window.addEventListener('resize', this.handleResize);

    if (typeof this.mobileMediaQuery.addEventListener === 'function') {
      this.mobileMediaQuery.addEventListener('change', this.handleMediaQueryChange);
    } else if (typeof this.mobileMediaQuery.addListener === 'function') {
      this.mobileMediaQuery.addListener(this.handleMediaQueryChange);
    }

    this.rebuild();
  }

  handleMediaQueryChange() {
    this.rebuild();
  }

  handleResize() {
    this.rebuild(false);
  }

  handleGridScroll() {
    if (!this.isSliderActive()) return;
    this.setActivePageFromScroll();
  }

  handlePointerDown(event) {
    if (!this.isSliderActive()) return;
    if (event.button !== 0) return;
    this.isPointerDown = true;
    this.dragMoved = false;
    this.isDragScrolling = false;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.startScrollLeft = this.gridEl.scrollLeft;
  }

  handlePointerMove(event) {
    if (!this.isPointerDown) return;
    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!this.isDragScrolling) {
      const isHorizontalIntent = absX > 10 && absX > absY + 2;
      if (!isHorizontalIntent) return;
      this.isDragScrolling = true;
      this.gridEl.classList.add('is-dragging');
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    this.dragMoved = true;
    this.gridEl.scrollLeft = this.startScrollLeft - deltaX;
  }

  handlePointerUp() {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.gridEl.classList.remove('is-dragging');
    if (this.isSliderActive() && this.isDragScrolling && this.dragMoved) {
      this.suppressClick = true;
    }
    this.isDragScrolling = false;
  }

  handleGridClickCapture(event) {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.suppressClick = false;
  }

  handleNativeDragStart(event) {
    if (!this.isSliderActive() || !this.isPointerDown) return;
    event.preventDefault();
  }

  isSliderActive() {
    return this.sliderEnabled && this.mobileMediaQuery.matches;
  }

  readSettingsFromDataset() {
    this.sliderEnabled = this.sectionEl.getAttribute('data-mobile-slider-enabled') === 'true';
  }

  computeSnapPoints() {
    const items = Array.from(this.gridEl.querySelectorAll('.limo-editorial-featured__item'));
    const maxScrollable = Math.max(0, this.gridEl.scrollWidth - this.gridEl.clientWidth);
    const unique = [];

    items.forEach((item) => {
      const point = Math.round(item.offsetLeft);
      if (point > maxScrollable + 1) return;
      const exists = unique.some((value) => Math.abs(value - point) <= 1);
      if (!exists) unique.push(point);
    });

    if (!unique.some((value) => Math.abs(value - maxScrollable) <= 1)) {
      unique.push(maxScrollable);
    }

    unique.sort((a, b) => a - b);
    this.snapPoints = unique;
  }

  rebuild(resetScroll = true) {
    this.readSettingsFromDataset();

    if (!this.isSliderActive()) {
      this.isPointerDown = false;
      this.dragMoved = false;
      this.isDragScrolling = false;
      this.suppressClick = false;
      this.gridEl.classList.remove('is-dragging');
      this.paginationEl.innerHTML = '';
      return;
    }

    this.computeSnapPoints();
    this.pageCount = this.snapPoints.length;
    this.activePage = 0;

    if (resetScroll) {
      this.gridEl.scrollLeft = 0;
    }

    this.renderPagination();
    this.setActivePageFromScroll();
  }

  renderPagination() {
    this.paginationEl.innerHTML = '';
    if (this.pageCount <= 1) return;

    for (let pageIndex = 0; pageIndex < this.pageCount; pageIndex += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'limo-editorial-featured__pagination-dot';
      dot.setAttribute('aria-label', `Go to slide ${pageIndex + 1}`);
      dot.addEventListener('click', () => this.scrollToPage(pageIndex));
      this.paginationEl.appendChild(dot);
    }
  }

  scrollToPage(pageIndex) {
    if (!this.isSliderActive()) return;
    const targetLeft = this.snapPoints[pageIndex];
    if (typeof targetLeft !== 'number') return;

    this.gridEl.scrollTo({
      left: targetLeft,
      behavior: 'smooth'
    });
  }

  setActivePageFromScroll() {
    if (this.pageCount <= 1 || this.snapPoints.length === 0) return;

    let nearestIndex = 0;
    let nearestDistance = Infinity;
    const currentScroll = this.gridEl.scrollLeft;

    for (let index = 0; index < this.snapPoints.length; index += 1) {
      const distance = Math.abs(this.snapPoints[index] - currentScroll);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    }

    this.activePage = Math.min(this.pageCount - 1, nearestIndex);
    this.updateActiveIndicator();
  }

  updateActiveIndicator() {
    const dots = Array.from(this.paginationEl.querySelectorAll('.limo-editorial-featured__pagination-dot'));
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activePage);
    });
  }

  destroy() {
    this.gridEl.removeEventListener('scroll', this.handleGridScroll);
    this.gridEl.removeEventListener('mousedown', this.handlePointerDown);
    this.gridEl.removeEventListener('click', this.handleGridClickCapture, true);
    this.gridEl.removeEventListener('dragstart', this.handleNativeDragStart);
    window.removeEventListener('mousemove', this.handlePointerMove);
    window.removeEventListener('mouseup', this.handlePointerUp);
    window.removeEventListener('resize', this.handleResize);
    if (typeof this.mobileMediaQuery.removeEventListener === 'function') {
      this.mobileMediaQuery.removeEventListener('change', this.handleMediaQueryChange);
    } else if (typeof this.mobileMediaQuery.removeListener === 'function') {
      this.mobileMediaQuery.removeListener(this.handleMediaQueryChange);
    }
    this.paginationEl.innerHTML = '';
  }
}

function initLimoEditorialFeaturedSection(sectionEl) {
  const sectionId = sectionEl.dataset.sectionId || sectionEl.id;
  if (!sectionId) return;
  if (window.LimoEditorialFeaturedSections[sectionId]) return;
  const instance = new LimoEditorialFeaturedMobileSlider(sectionEl);
  window.LimoEditorialFeaturedSections[sectionId] = instance;
}

function bootLimoEditorialFeaturedSections() {
  document.querySelectorAll('[data-limo-editorial-featured-section]').forEach((sectionEl) => {
    initLimoEditorialFeaturedSection(sectionEl);
  });
}

bootLimoEditorialFeaturedSections();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!target) return;
  const sectionEl = target.matches && target.matches('[data-limo-editorial-featured-section]')
    ? target
    : target.querySelector
      ? target.querySelector('[data-limo-editorial-featured-section]')
      : null;
  if (!sectionEl) return;
  initLimoEditorialFeaturedSection(sectionEl);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (!target) return;
  const sectionEl = target.matches && target.matches('[data-limo-editorial-featured-section]')
    ? target
    : target.querySelector
      ? target.querySelector('[data-limo-editorial-featured-section]')
      : null;
  if (!sectionEl) return;
  const sectionId = sectionEl.dataset.sectionId || sectionEl.id;
  if (!sectionId) return;
  const instance = window.LimoEditorialFeaturedSections[sectionId];
  if (instance && typeof instance.destroy === 'function') {
    instance.destroy();
  }
  delete window.LimoEditorialFeaturedSections[sectionId];
});
